
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Eye, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/providers/AuthProvider";
import { useFavorites } from "@/hooks/useFavorites";

export const ProductsCarousel = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toggleFavorite, isFavorite, isTogglingFavorite } = useFavorites();
  
  const { data: products, isLoading } = useQuery({
    queryKey: ["top-products"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("professional_products")
        .select(`
          *,
          profiles:professional_id (
            full_name,
            profile_photo
          )
        `)
        .limit(12);
      
      if (error) throw error;
      return data || [];
    },
  });

  const handleProductClick = (productId: string) => {
    navigate(`/services/products/${productId}`);
  };

  const handleFavoriteClick = async (e: React.MouseEvent, productId: string) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!user) {
      toast.error("Vous devez être connecté pour ajouter des favoris");
      return;
    }
    
    await toggleFavorite(productId);
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Card key={i} className="animate-pulse">
            <div className="aspect-square bg-muted rounded-t-lg"></div>
            <CardContent className="p-2">
              <div className="h-3 bg-muted rounded w-3/4 mb-1"></div>
              <div className="h-2 bg-muted rounded w-1/2 mb-2"></div>
              <div className="h-5 bg-muted rounded"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // Format price with the correct currency
  const formatPrice = (price: number, currency: string = "EUR") => {
    const currencySymbol = currency === "EUR" ? "€" : 
                          currency === "USD" ? "$" : 
                          currency === "FCFA" ? "FCFA" : 
                          currency === "GOURDE" ? "G" : 
                          currency;
    
    return currency === "FCFA" || currency === "GOURDE" ? 
          `${price} ${currencySymbol}` : 
          `${currencySymbol}${price}`;
  };

  if (!products?.length) {
    return (
      <Card className="p-4 text-center">
        <p className="text-sm text-muted-foreground">Aucun produit n'est disponible pour le moment.</p>
      </Card>
    );
  }

  return (
    <Carousel
      opts={{
        align: "start",
        loop: true,
      }}
      className="w-full"
    >
      <CarouselContent className="-ml-2 md:-ml-4">
        {products.map((product) => (
          <CarouselItem key={product.id} className="pl-2 md:pl-4 basis-1/2 md:basis-1/3 lg:basis-1/4 xl:basis-1/6">
            <div 
              className="border rounded-md overflow-hidden bg-white shadow-sm hover:shadow-md transition-all h-full flex flex-col group cursor-pointer"
              onClick={() => handleProductClick(product.id)}
            >
              <div className="relative aspect-square overflow-hidden bg-gray-100">
                {product.photo_url ? (
                  <img
                    src={product.photo_url}
                    alt={product.title}
                    className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <span className="text-[10px]">Aucune image</span>
                  </div>
                )}
                
                <div className="absolute top-1 right-1 bg-primary/90 px-1.5 py-0.5 rounded-full shadow-sm backdrop-blur-sm">
                  <span className="text-[10px] font-semibold text-primary-foreground">
                    {formatPrice(product.price, product.currency)}
                  </span>
                </div>
                
                <button
                  className={`absolute top-1 left-1 p-1 rounded-full transition-colors ${
                    isFavorite(product.id)
                      ? "bg-red-500 text-white" 
                      : "bg-white/80 text-gray-600 hover:bg-white"
                  }`}
                  onClick={(e) => handleFavoriteClick(e, product.id)}
                  disabled={isTogglingFavorite(product.id)}
                >
                  <Heart className={`h-2.5 w-2.5 ${isFavorite(product.id) && "fill-current"}`} />
                </button>
              </div>
              
              <div className="p-1.5 flex flex-col flex-grow">
                <h3 className="font-medium text-xs truncate">{product.title}</h3>
                <p className="text-[10px] text-muted-foreground truncate">
                  {product.description}
                </p>
                
                <div className="mt-auto">
                  <Button
                    variant="outline" 
                    size="sm" 
                    className="mt-1 text-[10px] py-0.5 h-5 w-full border-primary text-primary hover:bg-primary/5"
                  >
                    <Eye className="mr-1 h-2.5 w-2.5" /> Voir
                  </Button>
                </div>
              </div>
            </div>
          </CarouselItem>
        ))}
      </CarouselContent>
      <div className="flex justify-center mt-2">
        <CarouselPrevious className="relative inset-auto translate-y-0 mx-1 h-6 w-6" />
        <CarouselNext className="relative inset-auto translate-y-0 mx-1 h-6 w-6" />
      </div>
    </Carousel>
  );
};
